import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { FormGroup } from "reactstrap";
import { useToggleModal } from "../../../lib/hooks/useToggleModal";
import { useGlobalWorkingHours } from "../../../lib/hooks/useGlobalWorkingHours";

import { Icon } from "../../shared/Icon/Icon";
import { Input } from "../../shared/Input/Input";
import { LabelWithInformationBox } from "../../shared/LabelWithInformationBox/LabelWithInformationBox";
import { DefaultTemplate } from "../../shared/DefaultTemplate/DefaultTemplate";
import { Button } from "../../shared/Button/Button";
import { Switch } from "../../shared/Switch/Switch";
import { ViewMembersModal } from "./ViewMembersModal";
import { AddMembersModal } from "./AddMembersModal";
import { WayFinderFormWorkspaces } from "../../shared/Forms/WayFinderForm/shared/WayFinderFormWorkspaces/WayFinderFormWorkspaces";
import { GroupWorkspacePicker } from "./GroupWorkspacePicker/GroupWorkspacePicker";
import { WorkplaceFields } from "../../../api/graphql/workplaces";
import { tooltips } from "../../../lib/utils/tooltips";
import { GroupFormik } from "./lib/useGroupFormik";
import { GroupBookingWarning } from "./GroupBookingWarning";

import "./GroupForm.scss";

interface Props {
  formik: GroupFormik;
}

export const GroupForm = ({ formik }: PropsWithChildren<Props>) => {
  const [showViewMembersModal, setShowViewMembersModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const { toggleModal: toggleWorkspacePicker, isOpen: isWorkspacePickerOpen } =
    useToggleModal();
  const { deskBookingEnabled, roomBookingEnabled } = useGlobalWorkingHours();

  const handleDeleteWorkspace = (
    item: WorkplaceFields,
    type: "desks" | "rooms"
  ) => {
    const currentValues = formik.values[type];

    const updatedValues = currentValues.filter(
      (workplaceItem) => workplaceItem.id !== item.id
    );
    return formik.setFieldValue(type, updatedValues);
  };

  const clearWorkspaces = () => {
    formik.setFieldValue("rooms", []);
    formik.setFieldValue("desks", []);
    return;
  };

  const emptyWorkspaces = useMemo(() => {
    return formik.values.rooms.length <= 0 && formik.values.desks.length <= 0;
  }, [formik.values.rooms, formik.values.desks]);

  const handleAccessTypeChange = (accessEverything: boolean) => {
    if (formik.values.isDefault) {
      return;
    }

    formik.setFieldValue("accessEverything", accessEverything);
    if (accessEverything) {
      clearWorkspaces();
    }
  };

  return (
    <div className="GroupForm">
      {isWorkspacePickerOpen && (
        <>
          <GroupWorkspacePicker
            closePicker={toggleWorkspacePicker}
            formik={formik}
            clearWorkspaces={clearWorkspaces}
          />
          <div
            onClick={toggleWorkspacePicker}
            className="background__shadow"
          ></div>
        </>
      )}

      <form onSubmit={formik.handleSubmit}>
        <div className="GroupForm__subTitle ProfilesForm__type TemplateManage__subTitle flex-a-center mb-2 mt-1">
          <Icon className="TemplateManage__group-profile" icon="people_icon" />
          <h6 className="pl-1">Group</h6>
        </div>
        <DefaultTemplate>
          <FormGroup>
            <LabelWithInformationBox
              for="name"
              title="Display name"
              message={
                tooltips.groups?.displayName ||
                "Name of the group as shown to users."
              }
              direction="right"
            />
            <Input
              id="name"
              name="name"
              type="text"
              value={formik.values.name}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              isTouched={formik.touched.name}
              errorMessage={formik.errors.name}
              invalid={!!formik.errors.name && formik.touched.name}
            />
          </FormGroup>

          <FormGroup>
            <LabelWithInformationBox
              for="description"
              title="Description"
              message={
                tooltips.groups?.description ||
                "Description of the group's purpose and permissions."
              }
              direction="right"
            />
            <Input
              type="text"
              id="description"
              name="description"
              onBlur={formik.handleBlur}
              value={formik.values.description}
              onChange={formik.handleChange}
            />
          </FormGroup>
        </DefaultTemplate>

        <div className="flex-a-center flex-wrap justify-content-between">
          <div className="GroupForm__subTitle ProfilesForm__type TemplateManage__subTitle flex-a-center mb-3 mr-3">
            <Icon icon="people_icon_18x18" />
            <h6 className="pl-1">Members</h6>
          </div>

          <div className="d-flex flex-wrap gap-1">
            {formik.values.id && (
              <Button
                color="primary"
                size="small"
                title="View members"
                onClick={() => setShowViewMembersModal(true)}
                type="button"
                icon="people_icon_18x18"
                className="Button__members--view"
              />
            )}
            <Button
              color="outline-primary"
              size="small"
              title="Add members"
              onClick={() => setShowAddMembersModal(true)}
              type="button"
              icon="add-user-icon"
              className="Button__members--add"
            />
          </div>
        </div>

        <div className="GroupForm__subTitle ProfilesForm__type TemplateManage__subTitle flex-a-center mb-3">
          <Icon icon="key-icon" />
          <h6 className="pl-1">Permissions</h6>
        </div>

        <DefaultTemplate>
          <div className="Switch__border">
            <div className="position-relative mb-3">
              <Switch
                name="roomBookingEnabled"
                title="Allow room booking"
                value={
                  !roomBookingEnabled ? false : formik.values.roomBookingEnabled
                }
                message={tooltips.groups.roomBooking}
                direction="right"
                disabled={!roomBookingEnabled}
                onChange={(value) => {
                  formik.setFieldValue("roomBookingEnabled", value);
                  formik.setFieldTouched("roomBookingEnabled", true);
                }}
              />
              {!roomBookingEnabled && <GroupBookingWarning workspace="room" />}
            </div>
            <div className="position-relative">
              <Switch
                name="deskBookingEnabled"
                title="Allow desk booking"
                value={
                  !deskBookingEnabled ? false : formik.values.deskBookingEnabled
                }
                message={tooltips.groups.deskBooking}
                direction="right"
                disabled={!deskBookingEnabled}
                onChange={(value) => {
                  formik.setFieldValue("deskBookingEnabled", value);
                  formik.setFieldTouched("deskBookingEnabled", true);
                }}
              />
              {!deskBookingEnabled && <GroupBookingWarning workspace="desk" />}
            </div>
          </div>
        </DefaultTemplate>

        <div className="GroupForm__subTitle ProfilesForm__type TemplateManage__subTitle flex-a-center mb-3">
          <Icon icon="company_icon_18x18" />
          <h6 className="pl-1">Workspace items access</h6>
        </div>

        <DefaultTemplate>
          <div className="Switch__border">
            <Switch
              name="accessEverything"
              title="Access everything"
              value={formik.values.accessEverything}
              message="Group members have access to all workspace items."
              direction="right"
              onChange={(value) => handleAccessTypeChange(value)}
              className="mb-3"
            />

            <Switch
              name="setCustomAccess"
              title="Set custom access"
              value={!formik.values.accessEverything}
              message="Group members only have access to selected workspace items."
              direction="right"
              onChange={(value) => {
                handleAccessTypeChange(!value);
              }}
              className="mb-3"
              disabled={formik.values.isDefault}
            >
              <div className="mt-3">
                <label className="label__main pb-3">Add from workspaces</label>
                <Button
                  icon="workplace-picker"
                  title="Open workspace picker"
                  color={emptyWorkspaces ? "outline-primary" : "primary"}
                  size="small"
                  onClick={toggleWorkspacePicker}
                  className="WayFinderFrom__workspace--opener mb-2"
                  type="button"
                />

                <WayFinderFormWorkspaces
                  type="room"
                  data={formik.values.rooms}
                  handleWorkspace={handleDeleteWorkspace}
                  hideCounter
                  showDataLength
                />
                <WayFinderFormWorkspaces
                  type="desk"
                  data={formik.values.desks}
                  handleWorkspace={handleDeleteWorkspace}
                  hideCounter
                  showDataLength
                />
              </div>
            </Switch>
          </div>
        </DefaultTemplate>
      </form>

      {showViewMembersModal && formik.values.id && (
        <ViewMembersModal
          groupId={formik.values.id}
          formik={formik}
          onClose={() => setShowViewMembersModal(false)}
        />
      )}

      {showAddMembersModal && (
        <AddMembersModal
          formik={formik}
          onClose={() => setShowAddMembersModal(false)}
          mode={formik.values.id ? "edit" : "add"}
        />
      )}
    </div>
  );
};
